import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const PublicationTypeSection = ({ onUpdate, boostInput = false }) => {
  const { fetchCachedList, selectedIndex } = useBuild();
  const [currentPublicationType, setCurrentPublicationType] = useState('');
  const [boost, setBoost] = useState(1);
  const [publicationTypeOptions, setPublicationTypeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPublicationTypes = async () => {
      try {
        setLoading(true);
        setError(null);

        const endpoint = `/search/frequent_values/${selectedIndex}/publication_type`;
        const publicationTypesData = await fetchCachedList(endpoint);
        setPublicationTypeOptions(publicationTypesData);
      } catch (err) {
        console.error('Failed to fetch publication types:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPublicationTypes();
  }, [fetchCachedList, selectedIndex]);

  const handlePublicationTypeAction = (actionType) => {
    if (!currentPublicationType) return;
    
    const selectedType = publicationTypeOptions.find(t => t.id === currentPublicationType);
    if (!selectedType) return;
    
    const updateData = {
      action: actionType,
      filter: 'terms',
      field: 'publication_type',
      value: [currentPublicationType]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    setCurrentPublicationType(''); // Reset after adding
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load publication types: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Publication Type</InputLabel>
        <Select
          value={currentPublicationType}
          label="Publication Type"
          onChange={(e) => setCurrentPublicationType(e.target.value)}
          disabled={loading}
        >
          {publicationTypeOptions.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.id} ({type.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          inputProps={{ min: 0, step: 0.1 }}
          disabled={loading}
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handlePublicationTypeAction('include')}
          disabled={!currentPublicationType || loading}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handlePublicationTypeAction('boost')}
            disabled={!currentPublicationType || loading}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handlePublicationTypeAction('exclude')}
          disabled={!currentPublicationType || loading}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default PublicationTypeSection;
